import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Menu, PanelLeft, PanelRight } from "lucide-react";
import { Link } from "react-router-dom";

import { HeaderBreadcrumbs } from "@/components/HeaderBreadcrumps";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { CARD_CONTENT_INSET } from "../layout.constants";
import { AppLogo } from "../sidebar/LogoWrapper";
import {
  SIDEBAR_COLLAPSED_W,
  SIDEBAR_EXPANDED_W,
  SPRING,
} from "../sidebar/sidebar.constants";

interface HeaderBrandProps {
  isSidebarOpen: boolean;
  onToggleSidebar: () => void;
  isDesktop: boolean;
  /** Builder layout: there is no sidebar drawer to toggle and the logo is
   *  rendered by BuilderLayout itself. On desktop we drop the brand column
   *  (logo + toggle) — otherwise two logos appear side by side; on mobile we
   *  drop the dead hamburger and keep just the app icon. */
  noSidebar?: boolean;
}

/** Left side of the top bar: brand logo + sidebar toggle (in a column that
 *  mirrors the sidebar width), then breadcrumbs. */
export const HeaderBrand = ({
  isSidebarOpen,
  onToggleSidebar,
  isDesktop,
  noSidebar,
}: HeaderBrandProps) => {
  const { t } = useTranslation(["common"]);
  const toggleLabel = isSidebarOpen
    ? t("common:a11y.collapse_sidebar")
    : t("common:a11y.expand_sidebar");
  // Desktop icon-rail state: the brand shows just the app icon, revealing the
  // expand control on hover.
  const isCollapsed = isDesktop && !isSidebarOpen;

  const brandLogo = (
    <Link to="/" aria-label={t("common:a11y.go_to_home")} className="shrink-0">
      <AppLogo />
    </Link>
  );

  const toggleButton = (
    <button
      type="button"
      className="flex size-9 shrink-0 items-center justify-center rounded-xs hover:bg-muted transition-colors"
      onClick={onToggleSidebar}
      aria-label={toggleLabel}
    >
      {isSidebarOpen ? (
        <PanelLeft className="size-5 rtl-flip" strokeWidth={2} />
      ) : (
        <PanelRight className="size-5 rtl-flip" strokeWidth={2} />
      )}
    </button>
  );

  // Collapsed: single control showing the app icon, swapping to the expand
  // icon on hover; clicking it expands the sidebar.
  const collapsedControl = (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={onToggleSidebar}
            aria-label={toggleLabel}
            className="group relative mx-auto flex size-9 shrink-0 items-center justify-center rounded-xs hover:bg-muted transition-colors"
          >
            <img
              src="/icons/app-icon.svg"
              alt=""
              draggable={false}
              className="size-7 object-contain transition-opacity group-hover:opacity-0"
            />
            <PanelRight
              className="absolute size-5 opacity-0 transition-opacity group-hover:opacity-100 rtl-flip"
              strokeWidth={2}
            />
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom">{toggleLabel}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  // Mobile: compact app icon + hamburger drawer toggle to its right. In the
  // builder there is no drawer, so the hamburger is dropped and only the app
  // icon remains.
  if (!isDesktop) {
    return (
      <div className="flex items-center gap-2 min-w-0">
        {!noSidebar && (
          <button
            type="button"
            onClick={onToggleSidebar}
            aria-label={toggleLabel}
            className="flex size-9 shrink-0 items-center justify-center rounded-xs hover:bg-muted transition-colors"
          >
            <Menu className="size-5" strokeWidth={2} />
          </button>
        )}
        <Link
          to="/"
          aria-label={t("common:a11y.go_to_home")}
          className="shrink-0"
        >
          <img
            src="/icons/app-icon.svg"
            alt={t("common:chrome.app_logo")}
            className="size-7 object-contain"
          />
        </Link>
      </div>
    );
  }

  // Builder layout: it renders the logo itself, so skip the brand column
  // (logo + toggle) and show only breadcrumbs — no duplicate logo.
  if (noSidebar) {
    return (
      <div className="flex items-center min-w-0 ps-1">
        <HeaderBreadcrumbs />
      </div>
    );
  }

  // Desktop: brand column width = sidebar width − card inset, so its right
  // divider aligns with the sidebar's right edge below (both expanded and
  // collapsed). Animated with the SAME spring as the sidebar for lockstep.
  const railWidth = isSidebarOpen ? SIDEBAR_EXPANDED_W : SIDEBAR_COLLAPSED_W;
  const brandColWidth = railWidth - CARD_CONTENT_INSET;

  return (
    <div className="flex items-center min-w-0">
      <motion.div
        initial={false}
        animate={{ width: brandColWidth }}
        transition={SPRING}
        className="flex items-center justify-between gap-2 shrink-0 overflow-hidden border-e border-border pe-3"
      >
        {isCollapsed ? (
          collapsedControl
        ) : (
          <>
            {brandLogo}
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>{toggleButton}</TooltipTrigger>
                <TooltipContent side="bottom">{toggleLabel}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </>
        )}
      </motion.div>
      <div className="ps-3 min-w-0">
        <HeaderBreadcrumbs />
      </div>
    </div>
  );
};
